import { buildLaunchDarklyFlagUsedHandler } from '@sentry/react';
import { LDProvider } from 'launchdarkly-react-client-sdk';
import React, { createContext, useMemo, useState } from 'react';

import { getEnvVariable } from '~/dev/utils/getEnvVariable';

import { LD_ALL_USERS_CONTEXT } from './constants';
import { useExposeLDClient } from './useFlag';

interface FlagsIdentity {
  identifiedKey?: string;
  setIdentifiedKey?: (key: string) => void;
}

/** Which client LaunchDarkly has identified. Empty outside `FlagsProvider`. */
export const FlagsIdentityContext = createContext<FlagsIdentity>({});

// Exposes the LDClient to getFlag() for imperative non-React call sites
// (Redux thunks, permission checks). Must live inside LDProvider.
const LDClientBridge = () => {
  useExposeLDClient();
  return null;
};

export const FlagsProvider = ({ children }: React.PropsWithChildren) => {
  const [identifiedKey, setIdentifiedKey] = useState<string>();
  const identity = useMemo(
    () => ({ identifiedKey, setIdentifiedKey }),
    [identifiedKey],
  );

  return (
    <LDProvider
      clientSideID={getEnvVariable('LAUNCHDARKLY_CLIENT_SIDE_ID')}
      context={LD_ALL_USERS_CONTEXT}
      options={{
        // Real-time streaming via WebSocket is disabled; flags are fetched once
        // at init and re-evaluated on the next page load. Saves a persistent
        // connection for an app where flag changes don't need to be instant.
        streaming: false,
        // Pairs with Sentry.launchDarklyIntegration() in src/index.tsx so any
        // flags evaluated on this page get attached as context on Sentry events.
        inspectors: [buildLaunchDarklyFlagUsedHandler()],
      }}
      reactOptions={{ useCamelCaseFlagKeys: false }}
    >
      <LDClientBridge />
      <FlagsIdentityContext.Provider value={identity}>
        {children}
      </FlagsIdentityContext.Provider>
    </LDProvider>
  );
};
